import React, { useState } from "react";
import { ArrowRightIcon, BellRingIcon, Loader2Icon, MailIcon } from "lucide-react";
import { isValidEmail, subscribe } from "../utils/subscribe";
type FormState = {
  kind: "idle";
} | {
  kind: "invalid";
  message: string;
} | {
  kind: "submitting";
} | {
  kind: "done";
  message: string;
} | {
  kind: "failed";
  message: string;
};
export function NotifyForm() {
  const [email, setEmail] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [state, setState] = useState<FormState>({
    kind: "idle"
  });
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!email.trim()) {
      setState({
        kind: "invalid",
        message: "Please enter your email address."
      });
      return;
    }
    if (!isValidEmail(email)) {
      setState({
        kind: "invalid",
        message: "Please enter a valid email address, e.g. name@company.co.za."
      });
      return;
    }
    setState({
      kind: "submitting"
    });
    const result = await subscribe(email, honeypot);
    if (result === "subscribed") {
      setState({
        kind: "done",
        message: "Thank you — we'll email you as soon as we launch."
      });
      setEmail("");
    } else if (result === "not-configured") {
      setState({
        kind: "done",
        message: "Launch notifications aren't available just yet, so your email hasn't been saved. Please check back soon."
      });
    } else {
      setState({
        kind: "failed",
        message: "We couldn't save your email right now. Please try again, or contact info@liftoffinvestments.co.bw."
      });
    }
  };
  const isInvalid = state.kind === "invalid";
  const isSubmitting = state.kind === "submitting";
  const message = state.kind === "invalid" || state.kind === "done" || state.kind === "failed" ? state.message : "";
  return <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:gap-8" data-id="element-35" data-mp="2sctub4zp15uz">
      <div className="flex shrink-0 items-center gap-3.5" data-id="element-36" data-mp="2nd7hgwns1rmp">
        <BellRingIcon className="h-7 w-7 text-brand-red" strokeWidth={1.75} aria-hidden="true" data-id="element-37" data-mp="2nal1q9qqg5te" />
        <div data-id="element-38" data-mp="1kx6obkayjvfw">
          <h2 className="text-sm font-bold uppercase tracking-[0.08em] text-brand-white" data-id="element-39" data-mp="1p0uv40rrmn03">Be the first to know</h2>
          <p className="mt-0.5 text-[13px] text-brand-white/75" data-id="element-40" data-mp="1u07rbc4j16fc">Get notified when we launch.</p>
        </div>
      </div>

      <div className="hidden h-10 w-px bg-brand-red/60 lg:block" aria-hidden="true" data-id="element-41" data-mp="1yqxvwn54p58z" />

      <form onSubmit={handleSubmit} noValidate className="w-full max-w-[420px]" data-id="element-42" data-mp="23qalhfndwqf9">
        <div className="absolute -left-[9999px] h-px w-px overflow-hidden" aria-hidden="true" data-id="element-43" data-mp="28oua5ntd4gbb">
          <label htmlFor="notify-company" data-id="element-44" data-mp="2cqfqjj53vcq9">Company</label>
          <input id="notify-company" type="text" tabIndex={-1} autoComplete="off" value={honeypot} onChange={e => setHoneypot(e.target.value)} data-id="element-45" data-mp="2cqyziacs2qs5" />
        </div>
        <div className={`flex items-center rounded-md border bg-brand-charcoal/80 py-1.5 pl-4 pr-1.5 backdrop-blur-sm transition-colors duration-150 focus-within:border-brand-white/60 ${isInvalid ? "border-brand-red" : "border-brand-white/25"}`} data-id="element-46" data-mp="nbj4dyvov5l9">
          <MailIcon className="h-[18px] w-[18px] shrink-0 text-brand-white/70" strokeWidth={1.75} aria-hidden="true" data-id="element-47" data-mp="azgqnivmm0kn" />
          <label htmlFor="notify-email" className="sr-only" data-id="element-48" data-mp="13djtzptxdrd">
            Email address
          </label>
          <input id="notify-email" type="email" inputMode="email" autoComplete="email" placeholder="Enter your email address" value={email} onChange={e => {
          setEmail(e.target.value);
          if (state.kind !== "idle" && state.kind !== "submitting") setState({
            kind: "idle"
          });
        }} aria-invalid={isInvalid} aria-describedby="notify-status" className="min-w-0 flex-1 bg-transparent px-3 py-2 text-sm text-brand-white placeholder:text-brand-white/55 focus:outline-none" data-id="element-49" data-mp="yrdxswo4s4ff" />
          <button type="submit" disabled={isSubmitting} aria-label="Notify me when you launch" className="flex h-10 w-11 shrink-0 items-center justify-center rounded bg-brand-red text-brand-white transition-[transform,opacity] duration-150 ease-out hover:opacity-90 active:scale-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-white disabled:opacity-70" data-id="element-50" data-mp="x7u8ktoev8ya">
            {isSubmitting ? <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-51" data-mp="1kxojh5p66nfw" /> : <ArrowRightIcon className="h-5 w-5" strokeWidth={2} aria-hidden="true" data-id="element-52" data-mp="16xvubetakfa1" />}
          </button>
        </div>
        <p id="notify-status" aria-live="polite" className={`mt-2 min-h-[1.25rem] text-[13px] leading-snug ${isInvalid || state.kind === "failed" ? "text-brand-red" : "text-brand-white/80"}`} data-id="element-53" data-mp="1b1vafwantc2v">
          {message}
        </p>
      </form>
    </div>;
}